/**
 * @file TransferSkuPickerDialog.tsx
 * @description «Подбор» фасовки в панели перемещения — одиночный выбор из
 * остатков склада-источника (phase19.24 этап 1, шаг 1).
 *
 * `WmsSkuSelectDialog` сюда не подходит по той же причине, что и в сборке: он
 * ищет по всему каталогу, а перемещать можно только лежащее на складе. Список
 * уже загружен формой (`useTransferStockOptions`) — диалог не делает запросов,
 * только фильтрует.
 *
 * Вид строки общий с приёмкой и списанием (`SkuSelectRow`): клик выбирает
 * фасовку, «уже добавлена» уводит к строке документа.
 *
 * Фокус после закрытия — общий порядок `usePickerFinalFocus`: выбрали строку →
 * курсор в «Партию», закрыли без выбора → фокус остаётся в поле скана.
 */

import { Search } from "lucide-react";
import { useState, type RefObject } from "react";

import type { TransferStockSku } from "./useTransferStockOptions";
import { SkuSelectRow } from "../SkuSelectRow";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { usePickerFinalFocus } from "../../ui/usePickerFinalFocus";

interface Props {
  open: boolean;
  skus: TransferStockSku[];
  onSelect: (sku: TransferStockSku) => void;
  /** Фасовки, все партии которых уже стоят в документе. */
  alreadyAddedIds?: string[];
  /** Перейти к существующей строке документа по skuId. */
  onGoToExisting?: (skuId: string) => void;
  /** Поле панели, куда уходит курсор после выбора строки («Партия»). */
  nextFieldRef?: RefObject<HTMLElement | null>;
  onClose: () => void;
}

export function TransferSkuPickerDialog({
  open,
  skus,
  onSelect,
  alreadyAddedIds = [],
  onGoToExisting,
  nextFieldRef,
  onClose,
}: Props) {
  const focus = usePickerFinalFocus(nextFieldRef);
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? skus.filter(
        (sku) =>
          sku.code.toLowerCase().includes(needle) ||
          sku.name.toLowerCase().includes(needle),
      )
    : skus;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent fullScreen finalFocus={focus.finalFocus} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Выбрать фасовку</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-[56px] items-center gap-2 rounded-xl px-3 ring-ghost focus-within:ring-2 focus-within:ring-primary">
          <Search className="size-5 shrink-0 text-primary" aria-hidden />
          <input
            type="text"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Артикул или название"
            aria-label="Поиск по остаткам склада"
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-on-surface-variant"
          />
        </div>

        <ul className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto">
          {visible.map((sku) => (
            <SkuSelectRow
              key={sku.skuId}
              title={sku.name}
              code={sku.code}
              note={`${sku.totalQuantity} шт`}
              added={alreadyAddedIds.includes(sku.skuId)}
              onSelect={() => {
                focus.markPicked();
                onSelect(sku);
              }}
              onGoToExisting={
                onGoToExisting ? () => onGoToExisting(sku.skuId) : undefined
              }
            />
          ))}
          {visible.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-on-surface-variant">
              На складе нет остатка по запросу «{query.trim()}»
            </li>
          ) : null}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
